import { useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useSpring, useInView, AnimatePresence } from 'framer-motion';
import { ArrowUp, Server, Brain, FileText, MessageCircle, Shield, Plug, Cpu, Settings, ChevronRight, Check, Zap, Coins, RefreshCw, Gavel, Images } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../components/ThemeProvider';
import { DEFAULT_LANGUAGE } from '../../lib/i18n';
import { generateContactEmailLink } from '../../lib/emailTemplates';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { PlatformSection } from '../../components/PlatformSection';
import { AboutSection } from '../../components/AboutSection';
import { TeamSection } from '../../components/TeamSection';
import { NeuralBackground } from '../../components/marketing/NeuralBackground';
import { PlatformShowcase } from '../../components/marketing/PlatformShowcase';
import { LocalAiCatchup } from '../../components/marketing/LocalAiCatchup';
import { AriaResultsSection } from '../../components/marketing/AriaResultsSection';
import { CustomFinetuneSection } from '../../components/marketing/CustomFinetuneSection';
import { BunkerDemoTour } from '../../components/marketing/BunkerDemoTour';
import { ScreenshotGalleryModal } from '../../components/marketing/ScreenshotGalleryModal';
import bunkerLogoLight from '../../assets/bunker_ai_logo_transparent_light.png';
import bunkerLogoDark from '../../assets/bunker_ai_logo_transparent.png';
import upgradeCycleImage from '../../assets/upgrade-cycle.png';
import hiragImage from '../../assets/hirag.png';
import performanceImage from '../../assets/performance.jpg';
import aiLanguageImage from '../../assets/ai_language.png';
import documentAutomationImage from '../../assets/document.png';
import securityImage from '../../assets/security.png';
import noCloudImage from '../../assets/no_cloud.jpg';
import pricingImage from '../../assets/token.jpg';

const iconMap = {
  server: Server,
  brain: Brain,
  'file-text': FileText,
  'message-circle': MessageCircle,
  shield: Shield,
  plug: Plug,
  cpu: Cpu,
  settings: Settings,
  gavel: Gavel,
  zap: Zap,
  'coins': Coins,
  'refresh-ccw': RefreshCw,
};

interface ScrollSectionProps {
  section: {
    id: string;
    title: string;
    subtitle?: string;
    description: string;
    icon: string;
    features: string[];
    image?: string;
  };
  index: number;
  sectionImageMap?: Record<string, string>;
}

function ScrollSection({ section, index, sectionImageMap }: ScrollSectionProps) {
  const isEven = index % 2 === 0;
  const Icon = iconMap[section.icon as keyof typeof iconMap] || Brain;
  const ref = useRef(null);
  // Reveal once and stay (no jarring re-fade on scroll-up); smoother easing + a gentle rise.
  const isInView = useInView(ref, { once: true, amount: 0.25 });

  return (
    <motion.section
      ref={ref}
      id={section.id}
      className="min-h-screen flex items-center py-20 relative overflow-hidden"
      initial={{ opacity: 0, y: 48 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 48 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    >

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: isEven ? -100 : 100 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: isEven ? -50 : 50 }}
            transition={{ duration: 0.8, type: 'spring' }}
            className={`order-1 min-w-0 ${(!isEven) ? 'lg:order-1' : 'lg:order-2'}`}
          >
            {/* Icon */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={isInView ? { scale: 1, rotate: 0 } : { scale: 0.5, rotate: -90 }}
              transition={{ duration: 0.6, type: 'spring', delay: 0.1 }}
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent text-white mb-6 shadow-lg shadow-primary/30"
            >
              <Icon className="w-8 h-8" />
            </motion.div>

            {/* Subtitle */}
            {section.subtitle && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-sm font-semibold text-primary uppercase tracking-wider mb-3"
              >
                {section.subtitle}
              </motion.p>
            )}

            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-4xl sm:text-5xl font-bold text-foreground mb-6"
            >
              {section.title}
            </motion.h2>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="text-lg text-muted-foreground mb-8 leading-relaxed"
            >
              {section.description}
            </motion.p>

            {/* Features */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="space-y-3"
            >
              {section.features.map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
                  transition={{ duration: 0.4, delay: 0.6 + idx * 0.08 }}
                  className="flex items-start gap-3 text-foreground"
                >
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center mt-0.5">
                    <Check className="w-4 h-4 text-primary" />
                  </div>
                  <span className="break-words leading-relaxed">{feature}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, x: isEven ? 100 : -100 }}
            animate={isInView ? { opacity: 1, scale: 1, x: 0 } : { opacity: 0, scale: 0.85, x: isEven ? 50 : -50 }}
            transition={{ duration: 0.8, type: 'spring' }}
            className={`order-2 min-w-0 ${(!isEven) ? 'lg:order-2' : 'lg:order-1'}`}
          >
            <div className="relative group">
              {/* Decorative elements */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                className="absolute -top-4 -right-4 w-24 h-24 border-2 border-dashed border-primary/30 rounded-full"
              />
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
                className="absolute -bottom-4 -left-4 w-16 h-16 border-2 border-dotted border-accent/30 rounded-full"
              />

              {/* Main image */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-primary/20">
                {section.image || sectionImageMap?.[section.id] ? (
                  <div className="relative w-full h-[300px] sm:h-[400px] md:h-[500px]">
                    <img
                      src={sectionImageMap?.[section.id] || section.image}
                      alt={section.title}
                      className={`w-full h-full transition-transform duration-700 group-hover:scale-110 ${
                        section.id === 'hirag' || section.id === 'unlimited-tokens'
                          ? 'object-contain bg-background'
                          : 'object-cover'
                      }`}
                    />
                  </div>
                ) : (
                  <div className="w-full h-[300px] sm:h-[400px] md:h-[500px] bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                    <Icon className="w-24 h-24 text-primary/40" />
                  </div>
                )}


              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
}

function USPSection() {
  const { t } = useTranslation('home');
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <motion.section
      ref={ref}
      className="min-h-[60vh] flex items-center justify-center py-20 relative overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: isInView ? 1 : 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ delay: 0.2 }}
            className="text-4xl sm:text-5xl font-bold text-foreground mb-6"
          >
            {t('bunkerDetail.uspSection.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ delay: 0.4 }}
            className="text-xl text-muted-foreground leading-relaxed mb-8"
          >
            {t('bunkerDetail.uspSection.description')}
          </motion.p>
        </motion.div>
      </div>
    </motion.section>
  );
}

// Kept but hidden until owner approval (rendered usage is commented below).
// Exported so it is retained without tripping noUnusedLocals; will be replaced
// by the dark-first VsTable section in E24-S5.
export function ComparisonTable() {
  const { t } = useTranslation('home');
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const tableData = t('bunkerDetail.comparisonTable', { returnObjects: true }) as {
    title: string;
    rows: Array<{ feature: string; typical: boolean; bunker: boolean }>;
  };

  return (
    <motion.section
      ref={ref}
      className="min-h-[50vh] flex items-center justify-center py-10 sm:py-14 relative overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: isInView ? 1 : 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-primary/5" />
      <div className="container mx-auto px-3 sm:px-4 lg:px-6 relative z-10 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-4 sm:mb-6"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
            {tableData.title}
          </h2>
        </motion.div>

        {/* Mobile Card Layout */}
        <div className="sm:hidden space-y-3">
          {tableData.rows.map((row, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 5 }}
              transition={{ delay: 0.1 + index * 0.04, duration: 0.35 }}
              className="bg-card/60 dark:bg-card/40 backdrop-blur-md rounded-xl border border-accent/20 dark:border-accent/30 shadow-sm overflow-hidden"
            >
              <div className="p-3">
                {/* Feature name */}
                <h3 className="text-sm font-semibold text-foreground mb-2.5">
                  {row.feature}
                </h3>

                {/* Comparison indicators */}
                <div className="flex items-center justify-between gap-3">
                  {/* Typical provider */}
                  <div className="flex items-center gap-1.5 flex-1">
                    <span className="text-xs text-muted-foreground">Andere</span>
                    {row.typical ? (
                      <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/15 dark:bg-emerald-500/20 flex-shrink-0">
                        <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                    ) : (
                      <span className="text-rose-500 text-base">✕</span>
                    )}
                  </div>

                  {/* Bunker.ai */}
                  <div className="flex items-center gap-1.5 flex-1">
                    <span className="text-xs font-medium text-accent">bunker.ai</span>
                    {row.bunker ? (
                      <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-accent to-orange-600 shadow-sm shadow-accent/40 flex-shrink-0">
                        <Check className="w-3.5 h-3.5 text-white" />
                      </div>
                    ) : (
                      <span className="text-rose-500 text-base">✕</span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Desktop Table Layout */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.98 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="hidden sm:block bg-card/60 dark:bg-card/40 backdrop-blur-md rounded-xl border border-accent/20 dark:border-accent/30 shadow-sm overflow-hidden"
        >
          <table className="w-full">
            <thead>
              <tr className="border-b border-accent/20 dark:border-accent/30 bg-gradient-to-r from-accent/8 via-accent/5 to-accent/8">
                <th className="p-2.5 sm:p-3 text-left text-xs sm:text-sm font-bold text-foreground">
                  Feature
                </th>
                <th className="p-2.5 sm:p-3 text-center text-xs sm:text-sm font-bold text-muted-foreground">
                  Andere Anbieter
                </th>
                <th className="p-2.5 sm:p-3 text-center text-xs sm:text-sm font-bold">
                  <span className="bg-gradient-to-r from-accent to-orange-600 bg-clip-text text-transparent">bunker.ai</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {tableData.rows.map((row, index) => (
                <motion.tr
                  key={index}
                  initial={{ opacity: 0, x: -8 }}
                  animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -4 }}
                  transition={{ delay: 0.15 + index * 0.04, duration: 0.35 }}
                  className="border-b border-border/30 last:border-b-0 hover:bg-accent/[0.04] dark:hover:bg-accent/[0.08] transition-colors duration-200"
                >
                  <td className="p-2.5 sm:p-3 text-foreground font-medium text-xs sm:text-sm">
                    {row.feature}
                  </td>
                  <td className="p-2.5 sm:p-3 text-center">
                    {row.typical ? (
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        transition={{ type: 'spring', stiffness: 400 }}
                        className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-emerald-500/15 dark:bg-emerald-500/20"
                      >
                        <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600 dark:text-emerald-400" />
                      </motion.div>
                    ) : (
                      <motion.span
                        whileHover={{ scale: 1.1 }}
                        transition={{ type: 'spring', stiffness: 400 }}
                        className="inline-block text-rose-500 text-base sm:text-lg"
                      >
                        ✕
                      </motion.span>
                    )}
                  </td>
                  <td className="p-2.5 sm:p-3 text-center bg-gradient-to-r from-accent/[0.03] to-transparent">
                    {row.bunker ? (
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        transition={{ type: 'spring', stiffness: 400 }}
                        className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-accent to-orange-600 shadow-sm shadow-accent/40"
                      >
                        <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                      </motion.div>
                    ) : (
                      <motion.span
                        whileHover={{ scale: 1.1 }}
                        transition={{ type: 'spring', stiffness: 400 }}
                        className="inline-block text-rose-500 text-base sm:text-lg"
                      >
                        ✕
                      </motion.span>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        {/* Highlight badge */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 4 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="mt-4 text-center"
        >
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent/10 dark:bg-accent/15 border border-accent/20 text-accent text-xs font-medium">
            <Zap className="w-3 h-3" />
            {t('bunkerDetail.comparisonTable.highlightBadge') || 'Alle bunker.ai Features inklusive'}
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
}

// ParallaxBackground was replaced by the more vibrant, theme-aware
// VibrantBackground (components/marketing/VibrantBackground).

// Section ID to local image mapping
const sectionImageMap: Record<string, string> = {
  'no-cloud': noCloudImage,
  'no-upgrade-coercion': upgradeCycleImage,
  'hirag': hiragImage,
  'no-rate-limits': performanceImage,
  'intelligent-chat': aiLanguageImage,
  'document-automation': documentAutomationImage,
  'us-cloud-act': securityImage,
  'unlimited-tokens': pricingImage,
};

export default function BunkerDetail() {
  const { lang } = useParams<{ lang?: string }>();
  const { t } = useTranslation('home');
  const { t: tc } = useTranslation('common');
  const { theme } = useTheme();
  const currentLang = lang || DEFAULT_LANGUAGE;
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const isAutoScrollingRef = useRef(false);
  const autoScrollRef = useRef<number | null>(null);

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  // Show scroll-to-top button only after scrolling past hero section
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > window.innerHeight * 0.8);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top on mount — but honour hash deep-links (e.g. /de#aria, and the
  // /aria/<lang> + /<lang>/aria routes which redirect into #aria). Also reacts to
  // hash changes so in-app navigation to a deep link scrolls too.
  useEffect(() => {
    const scrollToHash = () => {
      const id = window.location.hash.slice(1);
      if (!id) return;
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    if (window.location.hash) {
      // Give the page a moment to paint before scrolling to the target.
      setTimeout(scrollToHash, 120);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    const onHashChange = () => setTimeout(scrollToHash, 60);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Cleanup auto-scroll on unmount
  useEffect(() => {
    return () => {
      if (autoScrollRef.current) {
        cancelAnimationFrame(autoScrollRef.current);
      }
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const stopAutoScroll = () => {
    isAutoScrollingRef.current = false;
    if (autoScrollRef.current) {
      cancelAnimationFrame(autoScrollRef.current);
      autoScrollRef.current = null;
    }
  };

  const startAutoScroll = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Stop any existing auto-scroll
    stopAutoScroll();

    isAutoScrollingRef.current = true;
    let lastTime = performance.now();
    const scrollSpeed = 0.05; // Pixels per millisecond (adjust for reading pace)

    const scroll = (currentTime: number) => {
      if (!isAutoScrollingRef.current) return;

      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;

      const scrollAmount = scrollSpeed * deltaTime;
      window.scrollBy({ top: scrollAmount });

      // Stop if reached bottom
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 10) {
        stopAutoScroll();
        return;
      }

      autoScrollRef.current = requestAnimationFrame(scroll);
    };

    autoScrollRef.current = requestAnimationFrame(scroll);
  };

  // Add event listeners to stop auto-scroll on user interaction
  useEffect(() => {
    const stopAndCleanup = (e: Event) => {
      // Don't stop if clicking the scroll indicator button
      const target = e.target as HTMLElement;
      if (target.closest('button[aria-label="Auto-scroll through page"]')) {
        return;
      }

      if (isAutoScrollingRef.current) {
        stopAutoScroll();
      }
    };

    document.addEventListener('click', stopAndCleanup);
    document.addEventListener('wheel', stopAndCleanup);
    document.addEventListener('touchstart', stopAndCleanup);
    document.addEventListener('keydown', stopAndCleanup);

    return () => {
      document.removeEventListener('click', stopAndCleanup);
      document.removeEventListener('wheel', stopAndCleanup);
      document.removeEventListener('touchstart', stopAndCleanup);
      document.removeEventListener('keydown', stopAndCleanup);
    };
  }, []);

  return (
    /* Transparent root (no bg-background) so the fixed NeuralBackground shows
       through behind the page content; <html> provides the base colour. */
    <div className="min-h-screen">
      {/* Neural-network + flowing-line animated background */}
      <NeuralBackground />

      {/* Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-accent origin-left z-50"
        style={{ scaleX }}
      />

      {/* Header - Full navigation */}
      <Header />

      {/* Hero Section */}
      <section id="hero" className="min-h-screen flex items-center justify-center relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 90, 0],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1.2, 1, 1.2],
              rotate: [90, 0, 90],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl"
          />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, type: 'spring' }}
            className="flex flex-col items-center justify-center"
          >
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6, type: 'spring' }}
              className="mb-6 w-full flex justify-center"
            >
              <img
                src={theme === 'dark' ? bunkerLogoDark : bunkerLogoLight}
                alt="bunker.ai"
                className="w-72 sm:w-96 md:w-md lg:w-xl drop-shadow-2xl"
              />
            </motion.div>

            <div className="w-full max-w-3xl mx-auto px-4 py-3 rounded-3xl dark:bg-slate-950/40 dark:backdrop-blur-sm dark:border dark:border-white/5">

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-muted-foreground max-w-3xl mx-auto px-4 mb-6 text-center"
            >
              {t('bunkerDetail.hero.description')}
            </motion.p>

            {/* Slogan */}
            {t('bunkerDetail.hero.slogan') && (
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                className="text-xl sm:text-2xl font-semibold text-primary max-w-3xl mx-auto px-4 mb-8 text-center"
              >
                {t('bunkerDetail.hero.slogan')}
              </motion.p>
            )}

            {/* Tagline */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-lg text-muted-foreground max-w-2xl mx-auto px-4 mb-10 text-center"
            >
              {t('bunkerDetail.hero.tagline')}
            </motion.p>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-24 w-full"
            >
              <a
                href={generateContactEmailLink(currentLang)}
                className="inline-flex items-center gap-2 px-8 py-4 border-2 border-primary text-primary rounded-full font-semibold text-lg hover:bg-primary hover:text-white transition-all duration-300"
              >
                {tc('contact.contactUs')}
              </a>
            </motion.div>
            </div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 cursor-pointer bg-transparent border-0 p-0"
            onClick={startAutoScroll}
            aria-label="Auto-scroll through page"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              className="flex flex-col items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
            >
              <span className="text-sm">{tc('common.scrollToExplore')}</span>
              <ChevronRight className="w-6 h-6 rotate-90" />
            </motion.div>
          </motion.button>
        </div>
      </section>

      {/* USP Section */}
      <USPSection />

      {/* Interactive product demo (built from the real bunker.ai UI) */}
      <section id="demo" className="py-16 sm:py-24 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 dark:bg-slate-950/35 dark:backdrop-blur-sm dark:rounded-2xl dark:p-8 dark:border dark:border-white/5"
          >
            <p className="text-sm font-semibold text-primary uppercase tracking-wider mb-3">{t('productTour.kicker')}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">{t('productTour.heading')}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('productTour.intro')}
            </p>
            <button
              type="button"
              onClick={() => setGalleryOpen(true)}
              className="mt-6 inline-flex items-center gap-2.5 rounded-full bg-orange-500 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-orange-500/30 transition-all duration-200 hover:bg-orange-400 hover:scale-105 hover:shadow-xl hover:shadow-orange-400/40 active:scale-95"
            >
              <Images size={16} aria-hidden="true" />
              {t('screenshots.title')}
            </button>
          </motion.div>
          <div className="mx-auto max-w-7xl">
            <BunkerDemoTour />
          </div>
        </div>
      </section>
      <ScreenshotGalleryModal open={galleryOpen} onClose={() => setGalleryOpen(false)} />

      {/* "Gaming-PC schlägt Rechenzentrum" — lokale Modelle holen auf (Deck-Port).
          Sitzt zwischen Demo und "So arbeitet bunker.ai". */}
      <LocalAiCatchup />

      {/* Animated platform presentation (ported from the bunker.ai deck) */}
      <PlatformShowcase />

      {/* Comparison Table - Hidden until approval */}
      {/* <ComparisonTable /> */}

      {/* Content Sections */}
      {[
        // Token-Budget zuerst: das stärkste Kostenargument führt die Sektionen an.
        t('bunkerDetail.sections.unlimitedTokens', { returnObjects: true }),
        t('bunkerDetail.sections.noCloud', { returnObjects: true }),
        t('bunkerDetail.sections.usCloudAct', { returnObjects: true }),
        t('bunkerDetail.sections.noRateLimits', { returnObjects: true }),
        t('bunkerDetail.sections.noUpgradeCoercion', { returnObjects: true }),
        t('bunkerDetail.sections.hirag', { returnObjects: true }),
        t('bunkerDetail.sections.documentAutomation', { returnObjects: true }),
        t('bunkerDetail.sections.intelligentChat', { returnObjects: true }),
      ].filter((section: any) => section && section.id).map((section: any, index) => (
        <ScrollSection
          key={section.id}
          section={section}
          index={index}
          sectionImageMap={sectionImageMap}
        />
      ))}

      {/* Platform / Use Cases Section (ends with the ARIA spotlight card) */}
      <PlatformSection />

      {/* ARIA fine-tune results — measured base→ARIA: fingerprint radar, scorecard, loss */}
      <AriaResultsSection />

      {/* "Get your own model trained, customized for you & your company" */}
      <CustomFinetuneSection />

      {/* About Section */}
      <AboutSection />

      {/* Team Section */}
      <TeamSection />

      {/* Footer */}
      <Footer />

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            onClick={scrollToTop}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 transition-colors duration-300 scroll-top-arrow"
            aria-label="Scroll to top"
          >
            <motion.div
              animate={{ y: [0, -12, 0] }}
                 transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
              className="flex flex-col items-center gap-1"
            >
              <ArrowUp className="w-12 h-12" />
              <span className="text-sm font-medium">Top</span>
            </motion.div>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
